import { Link as CKLink, LinkProps as CKLinkProps } from '@1debit/chimekit';
import { cn } from 'utils/styles';
import { getIconByKey } from '@1debit/icons';
import { Sys } from 'libs/contentful/props';
import { LinkTargets } from 'types/constants';
import { getLinkTarget } from 'utils/styles';
import { isInternalLink } from 'utils/linkHelpers';

export interface BaseLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  additionalParams?: Record<string, string>;
  asButton?: boolean;
  iconProps?: CKLinkProps['iconProps'];
  sys?: Sys;
  __typename?: string;
  children: React.ReactNode;
}

/**
 * BaseLink component - Core link functionality without analytics
 *
 * This component provides:
 * - Simple link rendering with icon support
 * - Button styling support
 * - External link detection and target handling
 *
 * Use this when:
 * - You need custom analytics tracking (like NavLink)
 * - You're building utility components that handle their own tracking
 * - You need a link without any analytics
 *
 * For standard links with analytics, use the Link component instead.
 */
export default function BaseLink({
  asButton = false,
  children,
  href,
  target,
  rel,
  iconProps,
  className,
  ...props
}: BaseLinkProps) {
  const variant = asButton ? 'primary' : undefined;
  const baseLinkStyles = 'text-current text-[size:inherit] font-(--link-weight-reset)';
  const showIcon = iconProps !== undefined;
  const Icon = showIcon ? getIconByKey(iconProps?.icon ?? 'chevron_down') : null;
  const iconLinkStyles = 'flex gap-2';

  // Determine if it's an external link
  const isExternal = !isInternalLink(href);

  // Determine link attributes
  const linkTarget = target ? getLinkTarget(target as LinkTargets) : isExternal ? '_blank' : undefined;
  const linkRel = rel || (isExternal ? 'noreferrer' : undefined);

  return (
    <CKLink asChild analyticsId={null} variant={variant}>
      <a
        href={href}
        target={linkTarget}
        rel={linkRel}
        className={cn(
          'text-current text-[size:inherit] font-(--link-weight-reset)',
          'no-underline',
          { [baseLinkStyles]: !asButton },
          { [iconLinkStyles]: showIcon },
          className,
        )}
        {...props}>
        {children}
        {showIcon && Icon && <Icon className="size-(--ck-icon-size)" />}
      </a>
    </CKLink>
  );
}
